import { useMemo, useState, Fragment } from "react";
import {
  Plus, Trash2, Link2, ChevronDown, ChevronUp, Split as SplitIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useFormatCurrency } from "@/lib/CurrencyContext";
import {
  CATEGORY_LABEL, filterActuals, newActualId, todayIso, refLabel,
} from "@/lib/actuals";
import ActualAllocationPicker from "@/components/ActualAllocationPicker";
import SplitReceiptDialog from "@/components/SplitReceiptDialog";

/**
 * Category-scoped actuals log with a quick-add bar, list and progress strip.
 *
 * Props:
 *  - project: full project (reads `actuals` list)
 *  - onChange(patch): writes back to project (merges { actuals: [...] })
 *  - category: one of purchase | interior | exterior | systems | soft | other
 *  - plannedTotal: number — planned cost for this category (for the progress bar)
 *  - title: heading
 *  - description: subtitle
 */
export default function ActualsLog({
  project,
  onChange,
  category,
  plannedTotal,
  title,
  description,
  topStrip = null,
  extraFilter = null,
}) {
  const fmt = useFormatCurrency();
  const actuals = project.actuals || [];
  const scoped = useMemo(() => {
    const base = filterActuals(actuals, category);
    return extraFilter ? base.filter(extraFilter) : base;
  }, [actuals, category, extraFilter]);
  const scopedCategorySum = useMemo(
    () =>
      filterActuals(actuals, category).reduce(
        (s, a) => s + (Number(a.amount) || 0),
        0
      ),
    [actuals, category]
  );
  const spent = scoped.reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const pct = plannedTotal > 0 ? (scopedCategorySum / plannedTotal) * 100 : 0;

  const [draft, setDraft] = useState({
    date: todayIso(),
    amount: "",
    note: "",
    ref_id: "",
  });
  const [allocOpen, setAllocOpen] = useState(false);
  const [editingAlloc, setEditingAlloc] = useState(null); // entry id being edited
  const [splitEntry, setSplitEntry] = useState(null);     // entry being split (or null)

  const resetDraft = () =>
    setDraft({ date: todayIso(), amount: "", note: "", ref_id: "" });

  const addEntry = () => {
    const amount = Number(draft.amount);
    if (!amount || amount <= 0) return;
    const next = [
      ...actuals,
      {
        id: newActualId(),
        date: draft.date || todayIso(),
        category,
        amount,
        note: draft.note || "",
        ref_id: draft.ref_id || "",
      },
    ];
    // Auto-flip status to active on first actual
    const statusPatch =
      actuals.length === 0 && project.status === "draft"
        ? { status: "active" }
        : {};
    onChange({ actuals: next, ...statusPatch });
    resetDraft();
    setAllocOpen(false);
  };

  const removeEntry = (id) => {
    onChange({ actuals: actuals.filter((a) => a.id !== id) });
  };

  const applySplit = (replacements) => {
    if (!splitEntry) return;
    const without = actuals.filter((a) => a.id !== splitEntry.id);
    onChange({ actuals: [...without, ...replacements] });
    setSplitEntry(null);
  };

  const updateEntry = (id, patch) => {
    onChange({
      actuals: actuals.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    });
  };

  const overBudget = pct > 100;
  const barColor = overBudget
    ? "bg-[#DC2626]"
    : pct > 85
    ? "bg-slate-brand-500"
    : "bg-slate-brand-300";

  return (
    <div className="space-y-5 fc-fade-in" data-testid={`actuals-${category}`}>
      {/* Header / progress */}
      <div className="fc-card p-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="fc-heading text-xl text-[#1C1C1C]">{title}</h2>
            {description && (
              <p className="text-xs text-[#78716C] mt-1 max-w-xl">{description}</p>
            )}
          </div>
          <div className="text-right">
            <div className="fc-label">{CATEGORY_LABEL[category]} spent</div>
            <div className={`fc-heading text-2xl mt-0.5 ${overBudget ? "text-[#DC2626]" : "text-[#1C1C1C]"}`} data-testid={`actuals-total-${category}`}>
              {fmt(scopedCategorySum)}
            </div>
            <div className="text-[11px] text-[#78716C] mt-0.5">
              of <span className="fc-mono">{fmt(plannedTotal)}</span> planned
              {plannedTotal > 0 && (
                <span className={`ml-2 ${overBudget ? "text-[#DC2626]" : "text-slate-brand-700"}`}>
                  {pct.toFixed(0)}%
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 w-full bg-black/[0.05] rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all`}
            style={{ width: `${Math.min(100, pct)}%` }}
            data-testid={`actuals-progress-${category}`}
          />
          {overBudget && (
            <div
              className="h-full bg-[#DC2626]/40 -mt-2"
              style={{ width: `${Math.min(40, pct - 100)}%`, marginLeft: "100%" }}
            />
          )}
        </div>
        {overBudget && (
          <div className="mt-2 text-xs text-[#DC2626]">
            Over planned budget by {fmt(scopedCategorySum - plannedTotal)}.
          </div>
        )}
      </div>

      {topStrip}

      {/* Quick add */}
      <div className="fc-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="fc-label">Log a spend</div>
          <button
            type="button"
            data-testid={`alloc-toggle-${category}`}
            onClick={() => setAllocOpen((v) => !v)}
            className="text-[11px] text-slate-brand-700 hover:text-slate-brand-500 inline-flex items-center gap-1 border border-slate-brand-200 bg-slate-brand-50 rounded-full h-6 px-2 transition-colors"
          >
            <Link2 className="w-3 h-3" />
            {draft.ref_id
              ? refLabel(draft.ref_id, project) || "Allocated"
              : "Allocate to a line…"}
            {allocOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {allocOpen && category !== "purchase" && category !== "other" && (
          <div className="mb-4 p-3 rounded-lg bg-slate-brand-50 border border-slate-brand-200" data-testid={`alloc-picker-${category}`}>
            <ActualAllocationPicker
              category={category}
              project={project}
              refId={draft.ref_id}
              onChange={(rid) => setDraft((d) => ({ ...d, ref_id: rid }))}
              testPrefix={`alloc-draft-${category}`}
            />
            <div className="text-[11px] text-slate-brand-700 mt-2">
              Optional — leave blank to log the spend at the category level only.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[140px_140px_1fr_auto] gap-2">
          <Input
            type="date"
            value={draft.date}
            onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
            className="fc-input h-10 rounded-md text-sm"
            data-testid={`actual-add-date-${category}`}
          />
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draft.amount}
            onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
            placeholder="Amount"
            className="fc-input h-10 rounded-md text-sm text-right fc-mono"
            data-testid={`actual-add-amount-${category}`}
            onKeyDown={(e) => { if (e.key === "Enter") addEntry(); }}
          />
          <Input
            value={draft.note}
            onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
            placeholder="What was it for? (e.g. tile supplier deposit)"
            className="fc-input h-10 rounded-md text-sm"
            data-testid={`actual-add-note-${category}`}
            onKeyDown={(e) => { if (e.key === "Enter") addEntry(); }}
          />
          <button
            type="button"
            data-testid={`actual-add-btn-${category}`}
            onClick={addEntry}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-md h-10 px-4 text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {/* Entries */}
      <div className="fc-card p-0 overflow-hidden">
        <div className="px-5 py-3 bg-slate-brand-50 border-b border-slate-brand-200 flex items-center justify-between">
          <div className="fc-label">
            Entries ({scoped.length}){extraFilter ? " · filtered" : ""}
          </div>
          <div className="fc-label">Total <span className="ml-1.5 text-[#1C1C1C] fc-mono text-sm">{fmt(spent)}</span></div>
        </div>
        {scoped.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#78716C]" data-testid={`actuals-empty-${category}`}>
            No entries yet. Log your first {CATEGORY_LABEL[category].toLowerCase()} spend above.
          </div>
        ) : (
          <>
          {/* Desktop table */}
          <div className="overflow-x-auto fc-no-scrollbar hidden md:block">
          <table className="w-full min-w-[560px] text-sm" data-testid={`actuals-table-${category}`}>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#A8A29E]">
                <th className="py-2.5 px-5 font-normal w-32">Date</th>
                <th className="py-2.5 px-2 font-normal">Allocated to / Note</th>
                <th className="py-2.5 px-2 font-normal w-32 text-right">Amount</th>
                <th className="py-2.5 px-5 font-normal w-10" />
              </tr>
            </thead>
            <tbody>
              {[...scoped].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((a) => {
                const allocLabel = refLabel(a.ref_id, project);
                const isEditing = editingAlloc === a.id;
                return (
                  <Fragment key={a.id}>
                    <tr
                      className="border-t border-black/[0.04]"
                      data-testid={`actual-row-${a.id}`}
                    >
                      <td className="py-2 px-5 text-[#57534E] fc-mono text-xs align-top">{a.date}</td>
                      <td className="py-2 px-2 align-top">
                        <button
                          type="button"
                          onClick={() => setEditingAlloc(isEditing ? null : a.id)}
                          data-testid={`row-alloc-chip-${a.id}`}
                          className={`inline-flex items-center gap-1 text-[11px] border rounded-full h-6 px-2 transition-colors ${
                            allocLabel
                              ? "bg-slate-brand-50 border-slate-brand-200 text-slate-brand-700 hover:bg-slate-brand-100"
                              : "bg-white border-black/[0.08] text-[#78716C] hover:border-slate-brand-200"
                          }`}
                        >
                          <Link2 className="w-3 h-3" />
                          {allocLabel || "Allocate"}
                        </button>
                        <Input
                          value={a.note || ""}
                          onChange={(e) => updateEntry(a.id, { note: e.target.value })}
                          placeholder="Note"
                          className="fc-input h-8 rounded-md text-xs mt-1 border-transparent hover:border-black/[0.08] focus:border-black/[0.15]"
                        />
                      </td>
                      <td className="py-2 px-2 text-right align-top">
                        <Input
                          type="number"
                          value={a.amount}
                          onChange={(e) => updateEntry(a.id, { amount: Number(e.target.value) || 0 })}
                          className="fc-input h-8 rounded-md text-xs text-right fc-mono border-transparent hover:border-black/[0.08] focus:border-black/[0.15]"
                        />
                      </td>
                      <td className="py-2 px-5 align-top">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setSplitEntry(a)}
                            data-testid={`actual-split-${a.id}`}
                            className="text-[#A8A29E] hover:text-slate-brand-700 transition-colors"
                            aria-label="Split this receipt"
                            title="Split this receipt across multiple line items"
                          >
                            <SplitIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeEntry(a.id)}
                            data-testid={`actual-remove-${a.id}`}
                            className="text-[#A8A29E] hover:text-[#DC2626] transition-colors"
                            aria-label="Delete entry"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isEditing && (
                      <tr className="bg-slate-brand-50/50">
                        <td />
                        <td colSpan={3} className="px-2 pb-3">
                          <div className="p-3 rounded-lg bg-slate-brand-50 border border-slate-brand-200">
                            <ActualAllocationPicker
                              category={category}
                              project={project}
                              refId={a.ref_id || ""}
                              onChange={(rid) => updateEntry(a.id, { ref_id: rid })}
                              testPrefix={`alloc-row-${a.id}`}
                            />
                            <div className="mt-2 text-right">
                              <button
                                type="button"
                                onClick={() => setEditingAlloc(null)}
                                className="text-[11px] text-[#1C1C1C] border border-black/[0.08] hover:border-black/[0.2] rounded-md h-7 px-2.5 inline-flex items-center"
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Mobile stacked-card layout — under md breakpoint */}
          <div className="md:hidden divide-y divide-black/[0.06]" data-testid={`actuals-mobile-list-${category}`}>
            {[...scoped].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((a) => {
              const allocLabel = refLabel(a.ref_id, project);
              const isEditing = editingAlloc === a.id;
              return (
                <div key={a.id} className="p-4" data-testid={`actual-card-${a.id}`}>
                  {/* Row 1: date · allocation chip · delete + amount stacked right */}
                  <div className="flex items-start gap-3">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#A8A29E] fc-mono pt-1.5 shrink-0">{a.date}</div>
                    <button
                      type="button"
                      onClick={() => setEditingAlloc(isEditing ? null : a.id)}
                      data-testid={`row-alloc-chip-mobile-${a.id}`}
                      className={`inline-flex items-center gap-1 text-[11px] border rounded-full h-6 px-2 transition-colors flex-1 min-w-0 ${
                        allocLabel
                          ? "bg-slate-brand-50 border-slate-brand-200 text-slate-brand-700 hover:bg-slate-brand-100"
                          : "bg-white border-black/[0.08] text-[#78716C] hover:border-slate-brand-200"
                      }`}
                    >
                      <Link2 className="w-3 h-3 shrink-0" />
                      <span className="truncate">{allocLabel || "Allocate"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSplitEntry(a)}
                      data-testid={`actual-split-mobile-${a.id}`}
                      className="text-[#A8A29E] hover:text-slate-brand-700 transition-colors shrink-0 mt-1"
                      aria-label="Split this receipt"
                      title="Split this receipt across multiple line items"
                    >
                      <SplitIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeEntry(a.id)}
                      data-testid={`actual-remove-mobile-${a.id}`}
                      className="text-[#A8A29E] hover:text-[#DC2626] transition-colors shrink-0 mt-1"
                      aria-label="Delete entry"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {/* Row 2: note + amount */}
                  <div className="grid grid-cols-[1fr_110px] gap-2 mt-2.5">
                    <Input
                      value={a.note || ""}
                      onChange={(e) => updateEntry(a.id, { note: e.target.value })}
                      placeholder="Note"
                      className="fc-input h-9 rounded-md text-sm"
                    />
                    <Input
                      type="number"
                      value={a.amount}
                      onChange={(e) => updateEntry(a.id, { amount: Number(e.target.value) || 0 })}
                      className="fc-input h-9 rounded-md text-sm text-right fc-mono"
                    />
                  </div>
                  {/* Allocation picker drawer */}
                  {isEditing && (
                    <div className="mt-3 p-3 rounded-lg bg-slate-brand-50 border border-slate-brand-200">
                      <ActualAllocationPicker
                        category={category}
                        project={project}
                        refId={a.ref_id || ""}
                        onChange={(rid) => updateEntry(a.id, { ref_id: rid })}
                        testPrefix={`alloc-mobile-${a.id}`}
                      />
                      <div className="mt-2 text-right">
                        <button
                          type="button"
                          onClick={() => setEditingAlloc(null)}
                          className="text-[11px] text-[#1C1C1C] border border-black/[0.08] hover:border-black/[0.2] rounded-md h-7 px-2.5 inline-flex items-center"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      <SplitReceiptDialog
        open={!!splitEntry}
        onOpenChange={(o) => { if (!o) setSplitEntry(null); }}
        entry={splitEntry}
        project={project}
        onApply={applySplit}
      />
    </div>
  );
}
